import { getUserAvailabilityService } from './getUserAvailabilityService';
import { CalendarEventsLoadingState } from '../types/CalendarEventsLoadingState';
import calendarEventFromBirthdayEvent from '../utils/calendarEventFromBirthdayEvent';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import getCalendarViewService from 'owa-calendar-services/lib/services/getCalendarViewService';
import getBirthdayCalendarViewService from 'owa-calendar-services/lib/services/getBirthdayCalendarViewService';
import { calendarEvent } from 'owa-calendar-event-converter';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { MailboxType } from 'owa-client-ids';
import { DateRange } from 'owa-datetime-utils';
import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import { trace } from 'owa-trace';
import {
    getCalendarEntry,
    getCalendarFolder,
    getCalendarIdByFolderId,
    getCalendarEntryByFolderId,
    isLocalCalendarEntry,
    isValidCalendarEntry,
    CalendarId,
} from 'owa-calendar-cache';
import CalendarEntry from 'owa-service/lib/contract/CalendarEntry';
import CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';

export type LoadingStateChangedCallback = (
    calendarId: CalendarId,
    loadingState: CalendarEventsLoadingState
) => void;

export async function loadCalendarEventsInDateRange(
    dateRange: DateRange,
    folderId: string,
    onLoadingStateChanged: LoadingStateChangedCallback,
    retryCount: number = 0
): Promise<CalendarEvent[]> {
    let topExecutingActionDatapoint = returnTopExecutingActionDatapoint();
    // adding customData to indicate that the top action call resulted in service call due to out of cache range call
    if (topExecutingActionDatapoint) {
        topExecutingActionDatapoint.addCustomData({
            outOfCacheRange: true,
        });
    }
    const calendarId = getCalendarIdByFolderId(folderId);
    onLoadingStateChanged(calendarId, 'Loading');
    if (isLocalCalendarEntry(folderId)) {
        let calendarEntry: CalendarEntry = getCalendarEntryByFolderId(folderId);
        if (calendarEntry.CalendarFolderType == CalendarFolderTypeEnum.BirthdayCalendar) {
            // invoke getBirthdayCalendarView
            return await getBirthdayCalendarView(
                dateRange,
                calendarId,
                folderId,
                onLoadingStateChanged,
                retryCount
            );
        } else {
            // Invoke the service to get the data for local calendars
            return await getCalendarView(
                dateRange,
                calendarId,
                folderId,
                onLoadingStateChanged,
                calendarId.mailboxInfo.type == MailboxType.GroupMailbox
                    ? calendarId.mailboxInfo.mailboxId
                    : null,
                retryCount
            );
        }
    } else {
        // Invoke the service to get the data for linked calendars
        let calendarEntry: LocalCacheForRemoteCalendarEntry = getCalendarEntry({
            id: calendarId,
        }) as LocalCacheForRemoteCalendarEntry;

        if (isValidCalendarEntry(calendarEntry)) {
            // if the calendarFolder is valid here, it means we already went through the calendarFolderConfiguration process and cached it
            let folder = getCalendarFolder({
                id: { id: calendarEntry.ItemId.Id, mailboxInfo: calendarId.mailboxInfo },
            });
            return await loadCalendarEventsUsingFolderId(
                folder,
                calendarEntry,
                dateRange,
                calendarId,
                onLoadingStateChanged,
                retryCount
            );
        }

        onLoadingStateChanged(calendarId, 'Failed');
        return null;
    }
}

async function loadCalendarEventsUsingFolderId(
    calendarFolder: CalendarFolder,
    calendarEntry: LocalCacheForRemoteCalendarEntry,
    dateRange: DateRange,
    calendarId: CalendarId,
    onLoadingStateChanged: LoadingStateChangedCallback,
    retryCount: number = 0
): Promise<CalendarEvent[]> {
    if (
        /* A linked calendar entry who has shared their primary calendar */
        (!calendarFolder && calendarEntry.SharedFolderId == null) ||
        /* A non- group linked calendar entry for whom one does not have permissions to access */
        (calendarEntry.hasOwnProperty('OwnerEmailAddress') &&
            !calendarEntry.IsGroupMailboxCalendar &&
            calendarFolder &&
            !calendarFolder.EffectiveRights) ||
        (calendarFolder &&
            !calendarFolder.EffectiveRights.Read &&
            calendarEntry.IsGeneralScheduleCalendar)
    ) {
        while (retryCount >= 0) {
            try {
                const response = await getUserAvailabilityService(
                    dateRange.start,
                    dateRange.end,
                    [calendarEntry.OwnerEmailAddress],
                    'DetailedMerged',
                    null,
                    calendarId
                );
                if (response.ResponseCode != 'NoError') {
                    retryCount--;
                } else {
                    onLoadingStateChanged(calendarId, 'Loaded');
                    let calendarEvents = response.Responses[0].CalendarView.Items.map(
                        calendarItem => calendarEvent(calendarItem)
                    );
                    return calendarEvents;
                }
            } catch (error) {
                trace.warn(
                    `[loadCalendarEventsInDateRange:loadCalendarEventsUsingFolderId: Exception occured: ${error}`
                );
                retryCount--;
            }
        }
        onLoadingStateChanged(calendarId, 'Failed');
        return null;
    } else {
        let folderId = calendarFolder
            ? calendarFolder.FolderId.Id
            : calendarEntry.SharedFolderId.Id;

        return await getCalendarView(
            dateRange,
            calendarId,
            folderId,
            onLoadingStateChanged,
            calendarEntry.OwnerEmailAddress,
            retryCount
        );
    }
}

async function getCalendarView(
    dateRange: DateRange,
    calendarId: CalendarId,
    folderId: string,
    onLoadingStateChanged: LoadingStateChangedCallback,
    emailAddress?: string,
    retryCount: number = 0
): Promise<CalendarEvent[]> {
    while (retryCount >= 0) {
        try {
            const response = await getCalendarViewService(
                dateRange.start,
                dateRange.end,
                folderId,
                calendarId,
                emailAddress,
                isFeatureEnabled('cal-surface-declinedMeetings')
            );
            if (response.ResponseCode != 'NoError') {
                retryCount--;
            } else {
                onLoadingStateChanged(calendarId, 'Loaded');
                let calendarEvents = response.Items.map(calendarItem =>
                    calendarEvent(calendarItem, calendarId.mailboxInfo)
                );
                return calendarEvents;
            }
        } catch (error) {
            trace.warn(
                `[loadCalendarEventsInDateRange:getCalendarView: Exception occured: ${error}`
            );
            retryCount--;
        }
    }
    onLoadingStateChanged(calendarId, 'Failed');
    return null;
}

async function getBirthdayCalendarView(
    dateRange: DateRange,
    calendarId: CalendarId,
    folderId: string,
    onLoadingStateChanged: LoadingStateChangedCallback,
    retryCount: number = 0
): Promise<CalendarEvent[]> {
    while (retryCount >= 0) {
        try {
            const response = await getBirthdayCalendarViewService(
                dateRange.start,
                dateRange.end,
                folderId,
                calendarId
            );
            if (response.ResponseCode != 'NoError') {
                retryCount--;
            } else {
                onLoadingStateChanged(calendarId, 'Loaded');
                let calendarEvents = response.BirthdayEvents.map(event =>
                    calendarEventFromBirthdayEvent(
                        event,
                        folderId,
                        dateRange,
                        calendarId.mailboxInfo
                    )
                );
                return calendarEvents;
            }
        } catch (error) {
            trace.warn(
                `[loadCalendarEventsInDateRange:getBirthdayCalendarView: Exception occured: ${error}`
            );
            retryCount--;
        }
    }
    onLoadingStateChanged(calendarId, 'Failed');
    return null;
}
